import React, { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LAYERS = [
  { key: "outline", label: "Contorno", color: "#0ea5e9" },
  { key: "holes", label: "Fori", color: "#dc2626" },
  { key: "slots", label: "Asole", color: "#d97706" },
  { key: "dims", label: "Quote", color: "#16a34a" },
  { key: "views", label: "Viste", color: "#7c3aed" },
  { key: "excluded", label: "Esclusi", color: "#64748b" },
];

const STATUS_STYLE = {
  EXPLICIT: "bg-emerald-100 text-emerald-800",
  GEOMETRICALLY_DETECTED: "bg-sky-100 text-sky-800",
  CALCULATED: "bg-indigo-100 text-indigo-800",
  DEDUCED: "bg-violet-100 text-violet-800",
  AMBIGUOUS: "bg-amber-100 text-amber-800",
  SOURCE_MISSING: "bg-zinc-100 text-zinc-600",
  PARSER_MISSED: "bg-red-100 text-red-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-900",
  NOT_APPLICABLE: "bg-zinc-100 text-zinc-500",
};

export const DeepReadOverlay = ({ deepRead, page, pageUrl, onFeatureAction, featureReviews }) => {
  const [visible, setVisible] = useState({ outline: true, holes: true, slots: true, dims: false, views: false, excluded: false });
  const [selected, setSelected] = useState(null);
  const [corrVal, setCorrVal] = useState("");
  const [addType, setAddType] = useState("");
  const [addVal, setAddVal] = useState("");
  const rulesIndex = deepRead?.applied_rules_index || {};

  const pg = useMemo(() => (deepRead?.pages || []).find((p) => p.page === page), [deepRead, page]);
  const rect = pg?.page_rect || [0, 0, 595, 842];
  const [x0, y0, x1, y1] = rect;
  const holes = (deepRead?.holes || []).filter((h) => h.page === page);
  const slots = (deepRead?.slots || []).filter((s) => s.page === page);
  const dims = (deepRead?.dimensions || []).filter((d) => d.page === page && !d.discarded);
  const views = pg?.segmentation?.views || [];
  const ext = deepRead?.lengths?.external_cut_perimeter;

  const box = (b, color, key, onClick, dash) => b && (
    <rect key={key} x={b[0]} y={b[1]} width={Math.max(b[2] - b[0], 2)} height={Math.max(b[3] - b[1], 2)}
      fill="none" stroke={color} strokeWidth={1.6} strokeDasharray={dash} style={{ cursor: onClick ? "pointer" : "default", pointerEvents: "all" }}
      onClick={onClick} />);

  const review = selected ? featureReviews?.[selected.feature_id?.replace(/\./g, "_")] : null;

  return (
    <div data-testid="deepread-overlay-wrap">
      <div className="flex gap-2 flex-wrap mb-1">
        {LAYERS.map((l) => (
          <label key={l.key} className="text-[11px] flex items-center gap-1" data-testid={`layer-toggle-${l.key}`}>
            <input type="checkbox" checked={!!visible[l.key]}
              onChange={() => setVisible((v) => ({ ...v, [l.key]: !v[l.key] }))} />
            <span style={{ color: l.color }}>{l.label}</span>
          </label>
        ))}
      </div>
      <div className="relative border border-border">
        {pageUrl && <img src={pageUrl} alt="pagina disegno" className="w-full block" data-testid="pdf-page-image" />}
        <svg className="absolute inset-0 w-full h-full" viewBox={`${x0} ${y0} ${x1 - x0} ${y1 - y0}`}
          preserveAspectRatio="none" style={{ pointerEvents: "none" }} data-testid="deepread-svg">
          {visible.views && views.map((v) => box(v.bbox, "#7c3aed", v.view_id, null, "6 3"))}
          {visible.outline && ext?.bbox && box(ext.bbox, "#0ea5e9", "outline", () => setSelected({ ...ext, feature_id: "external_contour", feature_type: "contorno esterno" }))}
          {visible.holes && holes.flatMap((h) => [
            box(h.bbox, "#dc2626", h.feature_id, () => setSelected(h)),
            ...(h.instances || []).map((i, k) => box(i.bbox, "#dc2626", `${h.feature_id}-i${k}`, () => setSelected(h), "3 2")),
          ])}
          {visible.slots && slots.map((s) => box(s.bbox, "#d97706", s.feature_id, () => setSelected(s)))}
          {visible.dims && dims.map((d) => box(d.bbox, "#16a34a", d.dim_id, () => setSelected({ ...d, feature_id: d.dim_id, feature_type: "quota" }), "2 2"))}
          {visible.excluded && (pg?.excluded_circles || []).map((e, i) => null)}
        </svg>
      </div>

      {onFeatureAction && (
        <div className="flex gap-1 items-center mt-2 border border-dashed border-border p-1.5" data-testid="add-missing-feature-row">
          <span className="text-[11px] font-semibold">Feature mancata dal motore:</span>
          <select className="h-6 border border-border text-[10px] bg-background" value={addType}
            onChange={(e) => setAddType(e.target.value)} data-testid="add-feature-type-select">
            <option value="">tipo…</option>
            <option value="hole">foro</option>
            <option value="slot">asola</option>
            <option value="bend">piega</option>
            <option value="opening">apertura</option>
            <option value="other">altro</option>
          </select>
          <Input className="h-6 w-32 text-[11px]" placeholder="es. Ø8 x4" value={addVal}
            onChange={(e) => setAddVal(e.target.value)} data-testid="add-feature-value-input" />
          <Button size="sm" variant="outline" className="h-6 px-2" disabled={!addType} data-testid="add-feature-btn"
            onClick={() => { onFeatureAction({ action: "add", feature_type: addType, corrections: { value: addVal }, note: "feature mancata dal motore (PARSER_MISSED)" }); setAddType(""); setAddVal(""); }}>
            Aggiungi (PARSER_MISSED)
          </Button>
        </div>
      )}

      {selected && (
        <div className="border border-border p-2 mt-2 text-xs space-y-1" data-testid="feature-detail-panel">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold" data-testid="feature-detail-id">{selected.feature_id}</span>
            <Badge className={STATUS_STYLE[selected.status] || ""} data-testid="feature-detail-status">{selected.status}</Badge>
            {review && <Badge className="bg-emerald-100 text-emerald-800" data-testid="feature-review-badge">operatore: {review.action}</Badge>}
            <Button size="sm" variant="ghost" className="h-5 px-1 ml-auto" onClick={() => setSelected(null)}>×</Button>
          </div>
          <div>
            {selected.feature_type === "hole" && <>Ø {selected.diameter_mm ?? "?"} mm · qty {selected.quantity} · {selected.subtype}{selected.thread ? ` · ${selected.thread}` : ""}</>}
            {selected.feature_type === "slot" && <>asola {selected.width_mm ?? "?"}×{selected.length_mm ?? "?"} mm · qty {selected.quantity} · interasse {selected.center_distance_mm ?? "?"}</>}
            {selected.feature_type === "quota" && <>{selected.raw_text} {selected.tolerance ? `(tol ${selected.tolerance})` : ""}</>}
            {selected.value != null && selected.feature_type !== "quota" && !["hole", "slot"].includes(selected.feature_type) && <>{selected.value} {selected.unit}</>}
          </div>
          <div className="text-muted-foreground">metodo: {selected.method} · confidenza: {selected.confidence} · vista: {selected.view || "—"}</div>
          {selected.evidence && <div className="text-muted-foreground truncate" title={selected.evidence}>evidenza: {selected.evidence}</div>}
          {(selected.applied_rule_ids || []).length > 0 && (
            <div className="flex gap-1 flex-wrap" data-testid="feature-applied-rules">
              {selected.applied_rule_ids.map((rid) => (
                <Badge key={rid} variant="outline" className="text-[9px] px-1 font-mono" title={rulesIndex?.[rid] || rid}>
                  {rid}{rulesIndex?.[rid] ? ` · ${rulesIndex[rid]}` : ""}
                </Badge>
              ))}
            </div>
          )}
          {selected.pricing_allowed !== undefined && (
            <div className={selected.pricing_allowed ? "text-emerald-700" : "text-red-700"} data-testid="feature-pricing-gate">
              prezzo: {selected.pricing_allowed ? "ammesso" : `BLOCCATO — ${selected.pricing_blocked_reason || "incertezza"}`}
            </div>
          )}
          {(selected.missing_inputs || []).length > 0 && <div className="text-amber-700">input mancanti: {selected.missing_inputs.join(", ")}</div>}
          {onFeatureAction && selected.feature_id !== "external_contour" && selected.feature_type !== "quota" && (
            <div className="flex gap-1 flex-wrap items-center pt-1 border-t border-border">
              <Button size="sm" variant="outline" className="h-6 px-2" data-testid="feature-confirm-btn"
                onClick={() => onFeatureAction({ feature_id: selected.feature_id, action: "confirm" })}>Conferma</Button>
              <Input className="h-6 w-24 text-[11px]" placeholder="Ø / LxW" value={corrVal} onChange={(e) => setCorrVal(e.target.value)} data-testid="feature-correct-input" />
              <Button size="sm" variant="outline" className="h-6 px-2" data-testid="feature-correct-btn" disabled={!corrVal}
                onClick={() => { onFeatureAction({ feature_id: selected.feature_id, action: "correct", corrections: { value: corrVal } }); setCorrVal(""); }}>Correggi</Button>
              <Button size="sm" variant="outline" className="h-6 px-2" data-testid="feature-reject-btn"
                onClick={() => onFeatureAction({ feature_id: selected.feature_id, action: "reject" })}>Rifiuta</Button>
              <Button size="sm" variant="outline" className="h-6 px-2" data-testid="feature-duplicate-btn"
                onClick={() => onFeatureAction({ feature_id: selected.feature_id, action: "duplicate_view", duplicate_of_view: selected.view })}>Vista duplicata</Button>
              <select className="h-6 border border-border text-[10px] bg-background" defaultValue="" data-testid="feature-classify-select"
                onChange={(e) => { if (e.target.value) { onFeatureAction({ feature_id: selected.feature_id, action: e.target.value }); e.target.value = ""; } }}>
                <option value="">stato…</option>
                <option value="missing">ASSENTE</option>
                <option value="unreadable">ILLEGGIBILE</option>
                <option value="not_applicable">NON APPLICABILE</option>
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DeepReadPanel = ({ deepRead, onFeatureAction }) => {
  if (!deepRead) return null;
  const cov = deepRead.coverage || {};
  const conflicts = (deepRead.consistency_findings || []).filter((f) => f.severity === "error");
  const lengths = deepRead.lengths || {};
  return (
    <div className="space-y-2" data-testid="deepread-panel">
      {deepRead.pricing_gate && (
        <div className={`border p-2 text-xs ${deepRead.pricing_gate.definitive_quote_allowed ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}
          data-testid="pricing-gate-banner">
          <span className="font-semibold">
            {deepRead.pricing_gate.definitive_quote_allowed ? "Preventivo definitivo AMMESSO" : "Preventivo definitivo BLOCCATO"}
          </span>
          {" · KB v"}{deepRead.knowledge_version}
          {!deepRead.pricing_gate.definitive_quote_allowed && (
            <div className="mt-0.5">
              {Object.entries(deepRead.pricing_gate.blocked_critical_fields || {}).map(([f, r]) => (
                <div key={f} className="text-[11px]" data-testid={`gate-blocked-${f}`}>• {f}: {r}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {conflicts.length > 0 && (
        <div className="border border-red-200 bg-red-50 p-2 space-y-1" data-testid="deepread-conflicts">
          <div className="text-xs font-semibold text-red-800">Conflitti rilevati ({conflicts.length})</div>
          {conflicts.map((c, i) => (
            <div key={i} className="text-[11px] text-red-800" data-testid={`conflict-${i}`}>• {c.message}</div>))}
        </div>
      )}
      <div className="text-xs font-semibold">Lunghezze separate</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {Object.entries(lengths).map(([k, v]) => (
          <div key={k} className="text-[11px] flex items-center gap-1" data-testid={`length-${k}`}>
            <span className="font-mono w-44 truncate" title={k}>{k}</span>
            <span>{v.value != null ? `${v.value} mm` : "—"}</span>
            <Badge className={`${STATUS_STYLE[v.status] || ""} text-[9px] px-1`}>{v.status}</Badge>
          </div>
        ))}
      </div>
      <div className="text-xs font-semibold pt-1">Copertura tecnica ({Object.keys(cov).length} target)</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 max-h-56 overflow-auto">
        {Object.entries(cov).map(([k, v]) => (
          <div key={k} className="text-[11px] flex items-center gap-1" data-testid={`coverage-${k}`}>
            <span className="font-mono w-40 truncate" title={k}>{k}</span>
            <Badge className={`${STATUS_STYLE[v.status] || ""} text-[9px] px-1`} data-testid={`coverage-status-${k}`}>{v.status}</Badge>
            {v.value != null && typeof v.value !== "object" && <span className="truncate">{String(v.value).slice(0, 18)}</span>}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground">
        analisi {deepRead.timing_ms_total} ms · chiamate esterne: {(deepRead.external_calls || []).length} · da rivedere: {(deepRead.review_required || []).length}
      </div>
    </div>
  );
};
